 
import styled from '@emotion/styled'
import cardsOne from '../../static/cardsOne.svg'
import cardsTwo from '../../static/cardsTwo.svg'
import { Layout, Head as HeadComponent } from '@leshen/gatsby-theme-sylex'
import React from 'react'
import Helmet from 'react-helmet'

import {
  Accordion,
  AvailabilityForm,
  Billboard,
  PackageCard,
  SplitContent,
  useMapi,
  VariableContent,
} from '@leshen/gatsby-theme-leshen'

import {
  Brandy,
  Column,
  Columns,
  Dropdown,
  Stack,
  Typography,
} from '@leshen/ui'

import { Link as GatsbyLink, graphql, navigate } from 'gatsby'
import reactStringReplace from 'react-string-replace'
import { StaticImage } from 'gatsby-plugin-image'

import SeoCtaBar from '../components/SeoCtaBar'
import getFullStateName from '../utils/getFullStateName'
import siteTheme from 'gatsby-theme-centurylink/theme'

export function Head({ data }) {
  const { Page_Path, Static_Number, State_Name, City } = data.pageInfo.edges[0].node
  
  const page = {
    pagePath: Page_Path,
    title: `Centurylink® Business Availability in ${City}, ${State_Name} | ${Static_Number}`,
    description: `Find CenturyLink Business internet, phone, bundles or fiber services in ${City}, ${State_Name} | Call ${Static_Number}.`,
    robots: 'index,follow',
  }
  return (
      <HeadComponent
          data={data}
          pageContext={{ page }}
      />
  )
}

const StateTemplate = ({ data }) => {
  const {
    Page_Path,
    Promo_Code,
    State,
    Hero_Copy,
    Hero_Disclaimer,
    Cards_Copy,
    Zip_Code_Disclaimer,
    Package_Cards_Copy,
    City_Dropdown_Copy,
    City_Dropdown_Disclaimer,
    FAQ_1,
    FAQ_2,
    FAQ_3,
    FAQ_4,
  } = data.pageInfo.edges[0].node
  const siteMetadata = data.site.siteMetadata
  const header = data.sylexHeader
  const footer = data.sylexFooter
  const page = {
    pagePath: Page_Path,
    promoCode: Promo_Code,
    header: header?.raw ? JSON.parse(header.raw) : null,
    footer: footer,
  }
  const State_Name = getFullStateName(State)

  const mapStateFlag = {
    az: (
        <StaticImage src="../images/stateFlags/az.png" alt="AZ" placeholder="blurred" />
    ),
    co: (
        <StaticImage src="../images/stateFlags/co.png" alt="CO" placeholder="blurred" />
    ),
    ia: (
        <StaticImage src="../images/stateFlags/ia.jpg" alt="IA" placeholder="blurred" />
    ),
    id: (
        <StaticImage src="../images/stateFlags/id.jpg" alt="ID" placeholder="blurred" />
    ),
    mn: (
        <StaticImage src="../images/stateFlags/mn.jpg" alt="MN" placeholder="blurred" />
    ),
    mt: (
        <StaticImage src="../images/stateFlags/mt.jpg" alt="MT" placeholder="blurred" />
    ),
    nd: (
        <StaticImage src="../images/stateFlags/nd.jpg" alt="ND" placeholder="blurred" />
    ),
    ne: (
        <StaticImage src="../images/stateFlags/ne.jpg" alt="NE" placeholder="blurred" />
    ),
    nm: (
        <StaticImage src="../images/stateFlags/nm.jpg" alt="NM" placeholder="blurred" />
    ),
    nv: (
        <StaticImage src="../images/stateFlags/nv.jpg" alt="NV" placeholder="blurred" />
    ),
    or: (
        <StaticImage src="../images/stateFlags/or.jpg" alt="OR" placeholder="blurred" />
    ),
    sd: (
        <StaticImage src="../images/stateFlags/sd.jpg" alt="SD" placeholder="blurred" />
    ),
    ut: (
        <StaticImage src="../images/stateFlags/ut.jpg" alt="UT" placeholder="blurred" />
    ),
    wa: (
        <StaticImage src="../images/stateFlags/wa.jpg" alt="WA" placeholder="blurred" />
    ),
    wy: (
        <StaticImage src="../images/stateFlags/wy.jpg" alt="WY" placeholder="blurred" />
    )
  }

  const stateImg = State.toLowerCase()

  const StateFlagImg = ({ stateImg }) => {
    return (
        mapStateFlag[stateImg] || <StaticImage stateImg={null}/>
    )
  }

  const mapStateHero = {
    az: (
        <StaticImage src="../images/stateHero/heroAz.png" alt="AZ" placeholder="blurred" />
    ),
    co: (
        <StaticImage src="../images/stateHero/heroCo.png" alt="CO" placeholder="blurred" />
    ),
    ia: (
        <StaticImage src="../images/stateHero/heroIa.jpg" alt="IA" placeholder="blurred" />
    ),
    id: (
        <StaticImage src="../images/stateHero/heroId.jpg" alt="ID" placeholder="blurred" />
    ),
    mn: (
        <StaticImage src="../images/stateHero/heroMn.jpg" alt="MN" placeholder="blurred" />
    ),
    mt: (
        <StaticImage src="../images/stateHero/heroMt.jpg" alt="MT" placeholder="blurred" />
    ),
    nd: (
        <StaticImage src="../images/stateHero/heroNd.jpg" alt="ND" placeholder="blurred" />
    ),
    ne: (
        <StaticImage src="../images/stateHero/heroNe.jpg" alt="NE" placeholder="blurred" />
    ),
    nm: (
        <StaticImage src="../images/stateHero/heroNm.jpg" alt="NM" placeholder="blurred" />
    ),
    nv: (
        <StaticImage src="../images/stateHero/heroNv.jpg" alt="NV" placeholder="blurred" />
    ),
    or: (
        <StaticImage src="../images/stateHero/heroOr.jpg" alt="OR" placeholder="blurred" />
    ),
    sd: (
        <StaticImage src="../images/stateHero/heroSd.jpg" alt="SD" placeholder="blurred" />
    ),
    ut: (
        <StaticImage src="../images/stateHero/heroUt.jpg" alt="UT" placeholder="blurred" />
    ),
    wa: (
        <StaticImage src="../images/stateHero/heroWa.jpg" alt="WA" placeholder="blurred" />
    ),
    wy: (
        <StaticImage src="../images/stateHero/heroWy.jpg" alt="WY" placeholder="blurred" />
    )
  }

  const StateHeroImg = ({ stateImg }) => {
     return (
         mapStateHero[stateImg] || <StaticImage stateImg={null}/>)
  }

  const { rotatedNumber } = useMapi()

  const getFAQ = (shortName) => {
    const faq = {
      QA1: {
        question: `Is CenturyLink Business available in ${State_Name}?`,
        answer: (
          <Typography variant="feature">
            Yes. CenturyLink Business is available in {State_Name}.{' '}
            <GatsbyLink to="/internet">Click here</GatsbyLink> to view internet
            packages and see if service is available in your area.
          </Typography>
        ),
      },
      QA2: {
        question: `Does Centurylink Business offer service in ${State_Name}?`,
        answer: (
          <Typography variant="feature">
            CenturyLink Business does offer service in {State_Name}. To speak to
            one of our customer service representatives and find out if service
            is available in your area, please call{' '}
            <a href={`tel:${rotatedNumber}`}>{rotatedNumber}</a>.
          </Typography>
        ),
      },
      QA3: {
        question: `Is CenturyLink Business an internet provider in ${State_Name}?`,
        answer: (
          <Typography variant="feature">
            Yes. CenturyLink Business is an internet provider in {State_Name}.
            To find out if services are available in your area contact us at{' '}
            <a href={`tel:${rotatedNumber}`}>{rotatedNumber}</a>.
          </Typography>
        ),
      },
      QA4: {
        question: `Can I get CenturyLink Business fiber internet in ${State_Name}? `,
        answer: (
          <Typography variant="feature">
            CenturyLink Business fiber internet is available in {State_Name}.
            Check to see if services are available in your area. You can check
            our{' '}
            <GatsbyLink to="/internet/small-business-fiber">
              fiber packages
            </GatsbyLink>{' '}
            and call to <a href={`tel:${rotatedNumber}`}>{rotatedNumber}</a>
          </Typography>
        ),
      },
      QP1: {
        question: `How much is CenturyLink Business internet in ${State_Name}?`,
        answer: (
          <Typography variant="feature">
            Internet service starts at $30/mo., but CenturyLink Business
            internet prices can vary based on the package you choose. Speed may
            not be available in your area. Paperless billing required. Taxes and
            fees apply.
          </Typography>
        ),
      },
      QP2: {
        question: `How much do CenturyLink Business packages cost in ${State_Name}? `,
        answer: (
          <Typography variant="feature">
            CenturyLink Business packages vary in cost depending on the package
            you choose and start at $30/mo. Speed may not be available in your
            area. Paperless billing required. Taxes and fees apply.
          </Typography>
        ),
      },
      QP3: {
        question: `What is the price of CenturyLink Business internet in ${State_Name}?`,
        answer: (
          <Typography variant="feature">
            CenturyLink Business fiber internet starts at $30/mo. in{' '}
            {State_Name}. Speed may not be available in your area. Paperless
            billing required. Taxes and fees apply.
          </Typography>
        ),
      },
      QP4: {
        question: `How much do CenturyLink Business internet and phone packages in ${State_Name} cost?`,
        answer: (
          <Typography variant="feature">
            CenturyLink Business internet starts at $30/mo. However, the price
            of bundling a phone service with your internet package will vary
            depending on the exact services you choose. Speed may not be
            available in your area. Paperless billing required. Taxes and fees
            apply.
          </Typography>
        ),
      },
      QC1: {
        question: `What is the number to reach CenturyLink Business in ${State_Name}?`,
        answer: (
          <Typography variant="feature">
            For now, you can reach CenturyLink Business in {State_Name} by
            calling CenturyLink customer service at{' '}
            <a href={`tel:${rotatedNumber}`}>{rotatedNumber}</a>.
          </Typography>
        ),
      },
      QC2: {
        question: `What is the number for CenturyLink Business in ${State_Name} to add a service?`,
        answer: (
          <Typography variant="feature">
            The phone number to call in order to add a CenturyLink Business
            service is <a href={`tel:${rotatedNumber}`}>{rotatedNumber}</a>.
          </Typography>
        ),
      },
      QC3: {
        question: `What is the moving phone number for CenturyLink Business in ${State_Name}?`,
        answer: (
          <Typography variant="feature">
            The moving phone number for CenturyLink Business in {State_Name} is{' '}
            <a href={`tel:${rotatedNumber}`}>{rotatedNumber}</a>.
          </Typography>
        ),
      },
      QC4: {
        question: `How do I contact CenturyLink Business in ${State_Name}?`,
        answer: (
          <Typography variant="feature">
            You can contact CenturyLink Business in {State_Name} by calling{' '}
            <a href={`tel:${rotatedNumber}`}>{rotatedNumber}</a> or chat with a
            member of our{' '}
            <GatsbyLink to="/business-support">
              customer support team
            </GatsbyLink>{' '}
            on our website.
          </Typography>
        ),
      },
      QB1: {
        question: `Does CenturyLink Business in ${State_Name} offer bundles with internet? `,
        answer: (
          <Typography variant="feature">
            Yes. Our internet services can come bundled in {State_Name}.{' '}
            <GatsbyLink to="/bundles">Click here</GatsbyLink> to view bundle
            packages!
          </Typography>
        ),
      },
      QB2: {
        question: `Can I get home phone with CenturyLink Business fiber internet in ${State_Name}?`,
        answer: (
          <Typography variant="feature">
            A home phone line can be added to your CenturyLink Business{' '}
            <GatsbyLink to="/internet/small-business-fiber">
              fiber internet package
            </GatsbyLink>{' '}
            in {State_Name}. Check to see if service is available in your area.
          </Typography>
        ),
      },
      QB3: {
        question: 'Does CenturyLink Business offer internet and home phone bundles?',
        answer: (
          <Typography variant="feature">
            Yes. CenturyLink Business offers affordable and reliable internet
            and home phone bundles. Check to see if service is available in your
            area and click to view our{' '}
            <GatsbyLink to="/bundles">bundle packages</GatsbyLink>!
          </Typography>
        ),
      },
      QB4: {
        question: `Does CenturyLink Business offer bundles in ${State_Name}?`,
        answer: (
          <Typography variant="feature">
            CenturyLink Business offers a variety of bundles for both home and
            business usage in {State_Name}. Check to see if service is available
            in your area and click to view our{' '}
            <GatsbyLink to="/bundles">bundle packages</GatsbyLink>!
          </Typography>
        ),
      },
      ALL1: {
        question: `Does CenturyLink Business offer TV and internet bundles in ${State_Name}?`,
        answer: (
          <Typography variant="feature">
            Currently, CenturyLink Business does not offer TV service.
          </Typography>
        ),
      },
      ALL2: {
        question: `Are Brightspeed and Centurylink in ${State_Name} the same company?`,
        answer: (
          <Typography variant="feature">
            No. Brightspeed and CenturyLink are not the same company.
          </Typography>
        ),
      },
    }
    return faq[shortName]
  }

  const faqSchemaData = (shortName, piece) => {
    const schemaFAQ = {
      QA1: {
        question: `Is CenturyLink Business available in ${State_Name}?`,
        answer: `Yes. CenturyLink Business is available in ${State_Name}. Click here to view internet packages! Services not available everywhere. Restrictions apply.`,
      },
      QA2: {
        question: `Does Centurylink Business offer service in ${State_Name}?`,
        answer: `CenturyLink Business does offer service in ${State_Name}. To speak to one of our customer service representatives and find out if service is available in your area, please call <a href='tel:${rotatedNumber}'>${rotatedNumber}</a>.`,
      },
      QA3: {
        question: `Is CenturyLink Business an internet provider in ${State_Name}?`,
        answer: `Yes. CenturyLink Business is an internet provider in ${State_Name}. To find out if services are available in your area, please contact us at <a href='tel:${rotatedNumber}'>${rotatedNumber}</a>.`,
      },
      QA4: {
        question: `Can I get CenturyLink Business fiber internet in ${State_Name}?`,
        answer: `CenturyLink Business fiber internet is available in ${State_Name}. Check to see if services are available in your area. You can check our fiber packages and call us at <a href='tel:${rotatedNumber}'>${rotatedNumber}</a>.`,
      },
      QP1: {
        question: `How much is CenturyLink Business internet in ${State_Name}?`,
        answer:
          'Internet service starts at $30/mo., but CenturyLink Business internet prices can vary based on the package you choose. Speed may not be available in your area. Paperless billing required. Taxes and fees apply.',
      },
      QP2: {
        question: `How much do CenturyLink Business packages cost in ${State_Name}?`,
        answer:
          'CenturyLink Business packages vary in cost depending on the package you choose and start at $30/mo. Speed may not be available in your area. Paperless billing required. Taxes and fees apply.',
      },
      QP3: {
        question: `What is the price of CenturyLink Business internet in ${State_Name}?`,
        answer: `CenturyLink Business fiber internet starts at $30/mo. in ${State_Name}. Speed may not be available in your area. Paperless billing required. Taxes and fees apply.`,
      },
      QP4: {
        question: `How much do CenturyLink Business internet and phone packages in ${State_Name} cost?`,
        answer: 'CenturyLink Business internet starts at $30/mo. However, the price of bundling a phone service with your internet package will vary depending on the exact services you choose. Speed may not be available in your area. Paperless billing required. Taxes and fees apply.',
      },
      QC1: {
        question: `What is the number to reach CenturyLink Business in ${State_Name}?`,
        answer: `For now, you can reach CenturyLink Business in ${State_Name} by calling CenturyLink customer service at <a href='tel:${rotatedNumber}'>${rotatedNumber}</a>.`,
      },
      QC2: {
        question: `What is the number for CenturyLink Business in ${State_Name} to add a service?`,
        answer: `The phone number to call in order to add a CenturyLink Business service is <a href='tel:${rotatedNumber}'>${rotatedNumber}</a>.`,
      },
      QC3: {
        question: `What is the moving phone number for CenturyLink Business in ${State_Name}?`,
        answer: `The moving phone number for CenturyLink Business in ${State_Name} is <a href='tel:${rotatedNumber}'>${rotatedNumber}</a>`,
      },
      QC4: {
        question: `How do I contact CenturyLink Business in ${State_Name}?`,
        answer: `You can contact CenturyLink Business in ${State_Name} by calling <a href='tel:${rotatedNumber}'>${rotatedNumber}</a> or chat with a member of our customer support team on our website.`,
      },
      QB1: {
        question: `Does CenturyLink Business in ${State_Name} offer bundles with internet?`,
        answer: `Yes. Our internet services can come bundled in ${State_Name}. Click here to view bundle packages!`,
      },
      QB2: {
        question: `Can I get home phone with CenturyLink Business fiber internet in ${State_Name}?`,
        answer: `A home phone line can be added to your CenturyLink Business fiber internet package in ${State_Name}. Check to see if service is available in your area.`,
      },
      QB3: {
        question: 'Does CenturyLink Business offer internet and home phone bundles?',
        answer: 'Yes. CenturyLink Business offers affordable and reliable internet and home phone bundles. Check to see if service is available in your area and click to view our <a href=\'/bundles\'>bundle packages</a>!',
      },
      QB4: {
        question: `Does CenturyLink Business offer bundles in ${State_Name}?`,
        answer: `CenturyLink Business offers a variety of bundles for both home and business usage in ${State_Name}. Check to see if service is available in your area and click to view our <a href='/bundles'>bundle packages</a>!`,
      },
      ALL1: {
        question: `Does CenturyLink Business offer TV and internet bundles in ${State_Name}?`,
        answer: 'Currently, CenturyLink Business does not offer TV service.',
      },
      ALL2: {
        question: `Are Brightspeed and Centurylink in ${State_Name} the same company?`,
        answer: 'No. Brightspeed and CenturyLink are not the same company.',
      },
    }
    return schemaFAQ?.[shortName]?.[piece]
  }

  const generateSchema = () => {
    const schema = `
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "${faqSchemaData(FAQ_1, 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData(FAQ_1, 'answer')}"
            }
          },
          {
            "@type": "Question",
            "name": "${faqSchemaData(FAQ_2, 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData(FAQ_2, 'answer')}"
            }
          },
          {
            "@type": "Question",
            "name": "${faqSchemaData(FAQ_3, 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData(FAQ_3, 'answer')}"
            }
          },
          {
            "@type": "Question",
            "name": "${faqSchemaData(FAQ_4, 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData(FAQ_4, 'answer')}"
            }
          },
          {
            "@type": "Question",
            "name": "${faqSchemaData('ALL1', 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData('ALL1', 'answer')}"
            }
          },
          {
            "@type": "Question",
            "name": "${faqSchemaData('ALL2', 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData('ALL2', 'answer')}"
            }
          }
        ]
      }
      `
    return schema
  }

  const getBundlesLink = (text) => {
    let returnValue = text

    if (text.includes('bundled services')) {
      returnValue = reactStringReplace(text, 'bundled services', (match) => (
        <GatsbyLink to="/bundles">{match}</GatsbyLink>
      ))
    } else if (text.includes('bundles to fit your every need')) {
      returnValue = reactStringReplace(
        text,
        'bundles to fit your every need',
        (match) => <GatsbyLink to="/bundles">{match}</GatsbyLink>
      )
    }

    return returnValue
  }

  const getInternetLink = (text) =>
    reactStringReplace(
      text,
      'CenturyLink Business Internet services',
      (match) => <GatsbyLink to="/internet">{match}</GatsbyLink>
    )

  const cityDropdownData = data.cities.edges
    .map((current) => ({
      label: current.node.City,
      value: current.node.Page_Path,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return (
    <Layout
      page={page}
      siteMetadata={siteMetadata}
      main={
        <>
          <Helmet>
            <script type="application/ld+json">{`${generateSchema()}`}</script>
          </Helmet>
          <Billboard
            customLink={GatsbyLink}
            mainContent={
              <>
                <Typography variant="h1">
                  CenturyLink Business Availability in {State_Name}
                </Typography>
                <Typography>{Hero_Copy}</Typography>
                {Hero_Disclaimer && <Brandy text={Hero_Disclaimer} />}
              </>
            }
            image={<StateHeroImg stateImg={stateImg} />}
          />
          <SeoCtaBar />
          <VariableContent
            style={{ backgroundColor: siteTheme.colors.light }}
            mainContent={
              <>
                <Typography variant="h2">
                  CenturyLink Business Internet & Voice in {State_Name}
                </Typography>
                <Typography> {getBundlesLink(Cards_Copy)}</Typography>
              </>
            }
            alignMainContent="center"
          >
            <Stack spacing="xxl">
              <Columns cardStyled>
                <Column image={
                  <img
                    className="svg-image"
                    alt="cardsOne"
                    src={cardsOne}
                />
                }
                >
                  <Typography variant="h4">
                    Internet that fits your needs.
                  </Typography>
                  <Typography>
                    <GatsbyLink to="/internet/speed-test">
                      Test your current internet
                    </GatsbyLink>{' '}
                    speed to find a business internet plan tailored to fit your
                    company’s needs.
                  </Typography>
                </Column>
                <Column
                    image={
                      <img
                          className="svg-image"
                          alt="cardsTwo"
                          src={cardsTwo}
                      />
                    }
                >
                  <Typography variant="h4">
                    A connection you can count on.
                  </Typography>
                  <Typography>
                    Keep business running smoothly with a reliable connection,
                    stable speeds, and 24/7{' '}
                    <GatsbyLink to="/business-support">
                      customer support.
                    </GatsbyLink>
                  </Typography>
                </Column>
              </Columns>
            </Stack>
          </VariableContent>
          <SplitContent
            customLink={GatsbyLink}
            image={
              <StateFlagImg stateImg={stateImg} />
            }
            mainContent={
              <>
                <Typography variant="h2">
                  Enter your zip code below to find business internet in{' '}
                  {State_Name}
                </Typography>
                <AvailabilityForm
                  label="Zip Code"
                  buttonText="Submit"
                  placeholderText="Zip Code"
                  buttonColor="primary"
                />
                <Brandy text={Zip_Code_Disclaimer} />
              </>
            }
          />{' '}
          <VariableContent
            style={{ backgroundColor: siteTheme.colors.light }}
            mainContent={
              <>
                <Typography variant="h2">
                  CenturyLink Business internet in {State_Name}
                </Typography>
                <Typography>{getInternetLink(Package_Cards_Copy)}</Typography>
              </>
            }
            alignMainContent="center"
          >
            <Stack spacing="xxl">
              <Columns>
                {data?.allBrandyPackage?.edges.map(({node}) => (
                    <PackageCard
                        label={ node?.label || node?.labelOffer }
                        packageData={ node }
                        key={node.id}
                    />
                ))}
              </Columns>
            </Stack>
          </VariableContent>
          <VariableContent
            style={{
              backgroundColor: siteTheme.colors.primary,
              paddingTop: 'calc(0vw + 48px)',
              paddingBottom: 'calc(0vw + 48px)',
            }}
            mainContent={
              <>
                <Typography variant="h2">
                  CenturyLink Business Internet and services are in all these{' '}
                  {State_Name} cities
                </Typography>
                <Typography>{City_Dropdown_Copy}</Typography>
                <Dropdown
                  options={cityDropdownData}
                  onChange={(e) => navigate(e.value)}
                  placeholderText="Select City"
                />
                {cityDropdownData?.map((current) => (
                  <StyledLink href={current.value} key={current.value}>
                    {current.label}
                  </StyledLink>
                ))}
                <Brandy text={City_Dropdown_Disclaimer} />
              </>
            }
            alignMainContent="center"
          />
          <VariableContent
            mainContent={<Typography variant="h2">FAQ</Typography>}
            alignMainContent="center"
          >
            <Stack spacing="xxl" alignMainContent="center">
              <Accordion
                items={[
                  {
                    title: getFAQ(FAQ_1).question,
                    content: getFAQ(FAQ_1).answer,
                  },
                  {
                    title: getFAQ(FAQ_2).question,
                    content: getFAQ(FAQ_2).answer,
                  },
                  {
                    title: getFAQ(FAQ_3).question,
                    content: getFAQ(FAQ_3).answer,
                  },
                  {
                    title: getFAQ(FAQ_4).question,
                    content: getFAQ(FAQ_4).answer,
                  },
                  {
                    title: getFAQ('ALL1').question,
                    content: getFAQ('ALL1').answer,
                  },
                  {
                    title: getFAQ('ALL2').question,
                    content: getFAQ('ALL2').answer,
                  },
                ]}
              />
            </Stack>
          </VariableContent>
        </>
      }
    />
  )
}

export default StateTemplate

const StyledLink = styled.a`
  display: none !important;
`

export const query = graphql`
  query StateTemplateQuery(
    $pagePath: String
    $stateAbbreviation: String
  ) {
    site {
      ...LeshenMetadata
    }
    pageInfo: allDatasetManagerBusinessCenturylinkAoa(
      filter: { Page_Path: { eq: $pagePath } }
    ) {
      edges {
        node {
          Page_Path
          Static_Number
          Promo_Code
          State
          Hero_Copy
          Hero_Disclaimer
          Cards_Copy
          Zip_Code_Disclaimer
          Package_Cards_Copy
          City_Dropdown_Copy
          City_Dropdown_Disclaimer
          FAQ_1
          FAQ_2
          FAQ_3
          FAQ_4
        }
      }
    }
    cities: allDatasetManagerBusinessCenturylinkAoa(
      filter: { State: { eq: $stateAbbreviation }, Area_Type: { eq: "city" } }
    ) {
      edges {
        node {
          City
          Page_Path
        }
      }
    }
    allBrandyPackage(filter: { brandy_id: { in: ["ctlb-internetlowtier", "ctlb-internetfiber"] }}){
      edges {
        node {
          __typename
          id
          brandy_id
          brand_token
          details
          labelOffer
          name
          price {
            amount
            before
            currency
            sale
            prefix
            duration
            suffix
            disclaimer {
              text
            }
          }
          bullets {
            text
            disclaimer {
              text
            }
          }
        }
      }
    }
    sylexHeader(contentful_id: { eq: "52hAJPhHu6FKTCJS3hkTLc" }) {
      ...SylexHeaderData
    }
    sylexFooter(contentful_id: { eq: "5wFJdXelBgkD78doJADgXD" }) {
      ...SylexFooterData
    }    
  }
`
